/**
 * 全局筛选器组件
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import moment from 'moment';
import { ReactSortable } from 'react-sortablejs';
import type { IColumnInfo, IOptionType } from '@dbl/core/dataSource';
import { deepClone } from '@/pages/datalab/utils/utils';
import { useFilterHandle } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/components/filters/useFilterHandle';
import { FilterItem } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/components/filters/filter_item';
import { getFirstColumn, getFlatOptions } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/utils';
import FilterLogic from '@/pages/datalab/components/UserFilter/FilterLogic';
import { LANGUAGE_EN } from '@home/_config/config';
import { TFilterLogic } from '../../types';
import { IGlobalFilterProps } from './types';
import { getAbFilterValues } from '../../service';
import { getSelector } from '@dbl/services/global';
import { processCountrySelectorData } from '../../utils';
import AddButton from '../AddButton';
import { ABExperimentFilterEntryMap } from '../../../BehaviorAnalysis/ExplorationAnalysis/components/configuration';
import './index.scss';

export const ALL_VALUE = '255';
const GlobalFilter: React.FC<IGlobalFilterProps> = (props) => {
  const {
    className,
    title,
    required,
    value,
    logic: propsLogic,
    allFilters,
    optionFilters,
    gameCode,
    onChange,
    onLogicChange
  } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 直接使用传入的 value
  const [filters, setFilters] = useState<IColumnInfo[]>(value || []);
  const [logic, setLogic] = useState<TFilterLogic>(propsLogic || 'and');
  // 地区和国家数据
  const [regionCountries, setRegionCountries] = useState<any[]>([]);
  const [popularList, setPopularList] = useState<string[]>([]);

  // 同步外部传入的 value 和 logic
  useEffect(() => {
    setFilters(value || []);
  }, [value]);

  useEffect(() => {
    if (propsLogic !== undefined) {
      setLogic(propsLogic);
    }
  }, [propsLogic]);

  // 通知父组件状态变化
  const notifyFun = (columnFilters: IColumnInfo[]) => {
    setFilters(columnFilters);
    onChange(columnFilters);
  };

  const handle = useFilterHandle(filters, notifyFun);
  const { addItem, setItems } = handle;

  const selectedItems = useMemo(() => filters.map((item) => item.column_name), [filters]);

  // 处理逻辑关系变化
  const handleLogicChange = (val: string | 'AND' | 'OR') => {
    const newLogic = val.toLowerCase() as TFilterLogic;
    setLogic(newLogic);
    onLogicChange?.(newLogic);
  };

  // 所有可选指标
  const flatOptions = useMemo(() => getFlatOptions(optionFilters || []), [optionFilters]);
  // 所有可选指标已添加完时禁用添加按钮
  const disableAddButton = useMemo(() => {
    return flatOptions?.length === filters?.length;
  }, [flatOptions, filters]);

  // 处理默认值
  const getColumnWithDefaults = (column: IColumnInfo) => {
    const newColumn = { ...(column || {}) };
    // 日期类型默认设置为7天前到昨天
    if (newColumn.filter_type === 'date') {
      newColumn.operation = 'between';
      newColumn.column_value = [moment().subtract(7, 'day').format('YYYY-MM-DD'), moment().subtract(1, 'day').format('YYYY-MM-DD')];
    } else if (newColumn?.filter_type === 'groupCascade') {
      newColumn.column_value = [ALL_VALUE];
    } else {
      newColumn.column_value = [];
      newColumn.isAll = true;
    }
    return newColumn;
  };

  // 处理添加筛选器
  const handleAddFilter = () => {
    if (disableAddButton) {
      return;
    }
    const firstColumn = getFirstColumn(allFilters || [], filters, flatOptions);
    if (!firstColumn) {
      return;
    }
    const newColumn = getColumnWithDefaults(firstColumn);
    addItem(newColumn);
  };

  // 获取地区和国家数据
  useEffect(() => {
    if (!gameCode) {
      return;
    }
    const fetchCountrySelector = async () => {
      try {
        const result: any = await getSelector({
          game_code: gameCode,
          selector_list: ["country", "area"],
        });
        const { regionCountries: newRegionCountries, popularList: newPopularList } = processCountrySelectorData(result);
        setRegionCountries(newRegionCountries);
        setPopularList(newPopularList);
      } catch (error) {
        console.warn('Failed to load country selector:', error);
      }
    };
    fetchCountrySelector();
  }, [gameCode]);

  // 自定义选项加载函数：使用 AB实验的 getAbFilterValues 接口
  const customOptionLoader = useCallback(async (column: IColumnInfo, currentGameCode: string): Promise<IOptionType[]> => {
    try {
      const mappingId = column.table_id;
      const optRes: any = await getAbFilterValues({
        game_code: currentGameCode || gameCode || '',
        mapping_id: Number(mappingId),
      });
      // 接口返回格式：直接返回数组，每个元素包含 code, label_cn, label_en
      if (Array.isArray(optRes?.values)) {
        return optRes?.values?.map((item: any) => ({
          ...(item || {}),
          text: item.label_cn || item.code || '',
          value: item.code || '',
          text_en: item.label_en || item.code || '',
        }));
      }
      return [];
    } catch (error) {
      return [];
    }
  }, [gameCode]);

  return (
    <div className={classNames('dbl-global-filter', className)}>
      {title && (
        <div className={classNames('dbl-global-filter-title', { 'is-required': required })}>
          {title}
        </div>
      )}
      <div className="dbl-global-filter-content">
        <div className="model-port">
          <div className="indicators-all-context">
            {filters.length >= 2 && (
              <FilterLogic
                value={logic?.toUpperCase()}
                isEn={isEn}
                onChange={handleLogicChange}
              />
            )}

            <div className="context-right">
              <ReactSortable<any>
                list={filters}
                setList={(list: IColumnInfo[]) => {
                  setItems(list);
                }}
              >
                {filters.map((column) => (
                  <FilterItem
                    fieldSelectClassName="dbl-ab-experiment-field-select"
                    key={`${column.table_id}.${column.column_name}`}
                    column={column}
                    optionFilters={optionFilters || []}
                    handle={handle}
                    selectedItems={selectedItems}
                    customOptionLoader={customOptionLoader}
                    regionCountries={regionCountries}
                    popularList={popularList}
                    handleContent={() => null}
                    filterEntryMap={ABExperimentFilterEntryMap}
                    onSelectItem={(newItemName: string) => {
                      const findColumn = (allFilters || []).find((item: IColumnInfo) => item.column_name === newItemName);
                      if (!findColumn || findColumn.column_name === column.column_name) {
                        return;
                      }
                      const position = filters.indexOf(column);
                      if (position < 0) {
                        return;
                      }
                      const newColumn = getColumnWithDefaults(findColumn);
                      const list = [
                        ...filters.slice(0, position),
                        { ...deepClone(newColumn) },
                        ...filters.slice(position + 1, filters.length),
                      ];
                      setItems(list);
                    }}
                  />
                ))}
              </ReactSortable>
            </div>
          </div>
        </div>
        <AddButton
          className={classNames('dbl-global-filter-add-button', { 'margin-t-10': filters?.length > 0 })}
          text={formatMessage({ id: 'dbl_ab_experiment_global_filter_add_button' })}
          onClick={handleAddFilter}
          disabled={disableAddButton}
        />
      </div>
    </div>
  );
};

export default GlobalFilter;
